import { useEffect } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { useLanguage, type Language } from "@/contexts/LanguageContext";
import SEOHead from "@/components/SEOHead";
import NotFound from "@/pages/NotFound";

const VALID_LOCALES = ["pt", "es"] as const;
type LocaleParam = (typeof VALID_LOCALES)[number];

const localeToLanguage: Record<LocaleParam, Language> = {
  pt: "PT",
  es: "ES",
};

export const isValidLocale = (l?: string): l is LocaleParam =>
  !!l && (VALID_LOCALES as readonly string[]).includes(l);

const I18nLayout = () => {
  const { locale } = useParams<{ locale?: string }>();
  const { pathname, search, hash } = useLocation();
  const { setLanguage, language } = useLanguage();

  // "/en/*" is a duplicate of the unprefixed English URL — redirect permanently.
  const isEnPrefix = locale === "en";

  // `locale` is undefined on unprefixed English URLs (/contact) — that is valid.
  // A defined-but-unrecognised value (/banana/contact) is not.
  const hasInvalidLocale =
    locale !== undefined && !isEnPrefix && !isValidLocale(locale);

  useEffect(() => {
    if (hasInvalidLocale || isEnPrefix) return;
    if (isValidLocale(locale)) {
      const target = localeToLanguage[locale];
      if (target !== language) setLanguage(target);
    } else if (language !== "EN") {
      setLanguage("EN");
    }
  }, [locale, language, setLanguage, hasInvalidLocale, isEnPrefix]);

  if (isEnPrefix) {
    const stripped = pathname.replace(/^\/en(?=\/|$)/i, "") || "/";
    return <Navigate to={`${stripped}${search}${hash}`} replace />;
  }

  // Render 404 WITHOUT SEOHead, so no canonical or hreflang is emitted for a junk URL.
  if (hasInvalidLocale) return <NotFound />;


  return (
    <>
      <SEOHead />
      <Outlet />
    </>
  );
};

export default I18nLayout;
